import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getData, postData } from '@/features/shared/lib/api';
import { buildListParams } from '@/features/shared/lib/utils';
import Paginator from '@/features/shared/ui/Paginator';
import PublicationCard from '@/features/publication/components/PublicationCard';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

/** scholar 배열에서 현재 사용자(scholar_id)의 self_confirm 값 반환 */
function getSelfConfirm(item, currentScholarId) {
  if (!item?.scholar || !Array.isArray(item.scholar) || !currentScholarId) return item?.self_confirm ?? null;
  const me = item.scholar.find((s) => String(s?.scholar_id) === String(currentScholarId));
  return me?.self_confirm ?? item?.self_confirm ?? null;
}

export default function PublicationsTab() {
  const tabRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  const userId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all', 'confirmed', 'unconfirmed'
  const [pageInfo, setPageInfo] = useState({
    fromPage: 1,
    totalPage: 1,
    resultsPerPage: 20,
    totalResults: 0,
  });
  const [totalPapers, setTotalPapers] = useState(0);
  const [confirmedPapers, setConfirmedPapers] = useState(0);
  const [unconfirmedPapers, setUnconfirmedPapers] = useState(0);

  // 모달 상태
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [editCategory, setEditCategory] = useState('내 논문이 아닙니다.');
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 성공 팝업 상태
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchList = useCallback(async (pageNum = 1, filterVal = filter) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    try {
      setLoading(true);
      setErr(null);
      const extra = {
        ks: '',
        sub_ks: '',
        order_by: 'publish_date',
        order: 'desc',
        my_library: true,
      };
      if (filterVal === 'confirmed') {
        extra.self_confirm = 'Y';
      } else if (filterVal === 'unconfirmed') {
        extra.self_confirm = 'N';
      }
      const params = buildListParams({
        page: pageNum,
        perPage: 5,
        extra,
      });
      const res = await getData('/rm/v1/my_library/resources', params);
      if (!mounted) return;

      const result = res || {};
      const records = Array.isArray(result.records) ? result.records : [];
      const pi = result.pageInfo || {};
      const meta = result.meta || {};

      setRows(records);
      setPageInfo({
        fromPage: pi?.current_page ?? pi?.fromPage ?? pageNum,
        totalPage: pi?.totalPage ?? pi?.total_page ?? 1,
        resultsPerPage: pi?.resultsPerPage ?? pi?.per_page ?? params.record_per_page ?? 20,
        totalResults: pi?.totalResults ?? pi?.total_results ?? 0,
      });

      // meta: { total, confirmed, unconfirmed } 연결
      setTotalPapers(meta.total ?? pi?.totalResults ?? pi?.total_results ?? 0);
      setConfirmedPapers(meta.confirmed ?? 0);
      setUnconfirmedPapers(meta.unconfirmed ?? 0);
    } catch (e) {
      if (!mounted) return;
      setErr(e?.response?.data?.message || e.message);
    } finally {
      if (mounted) {
        setLoading(false);
        isFirstLoadRef.current = false;
      }
    }
  }, [userId, filter]);

  useEffect(() => {
    fetchList(page, filter);
  }, [fetchList, page, filter]);

  const onPage = (p) => {
    if (!p || p === pageInfo.fromPage) return;
    setPage(p);
    fetchList(p);
    setTimeout(() => {
      if (tabRef.current) {
        try {
          const rect = tabRef.current.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - 100;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        } catch (e) { /* no-op */ }
      }
    }, 100);
  };

  // 필터링된 리스트 (self_confirm은 scholar 배열 내 현재 사용자 기준)
  const filteredRows = rows.filter((item) => {
    const sc = getSelfConfirm(item, userId);
    if (filter === 'confirmed') {
      return sc === 'Y' || sc === 'y' || sc === true;
    } else if (filter === 'unconfirmed') {
      return sc !== 'Y' && sc !== 'y' && sc !== true;
    }
    return true;
  });

  // 탭 전환 시 기존 데이터 유지: loading 중에는 이전 rows 표시, 완료 후 filteredRows 표시
  const displayRows = loading ? rows : filteredRows;

  // "예" 버튼 클릭 핸들러
  const handleConfirm = async (recordId) => {
    try {
      setSubmitting(true);
      const res = await postData('/rm/v1/my_library/resource', {
        record_id: String(recordId),
        self_confirm: 'Y',
      });

      if (res?.success === true || res?.status === true) {
        setRows(prevRows =>
          prevRows.map(item => {
            const id = item.record_id || item.resource_id || item.id;
            if (String(id) === String(recordId)) {
              const scholars = Array.isArray(item.scholar) ? item.scholar : [];
              const updatedScholar = scholars.map((s) =>
                String(s?.scholar_id) === String(userId) ? { ...s, self_confirm: 'Y' } : s
              );
              return { ...item, scholar: updatedScholar, self_confirm: 'Y' };
            }
            return item;
          })
        );
        setConfirmedPapers(prev => prev + 1);
        setUnconfirmedPapers(prev => Math.max(0, prev - 1));
        setSuccessMessage('감사합니다');
        setSuccessModalOpen(true);
        await fetchList(page);
      }
    } catch (e) {
      alert('처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  // "아니요" 버튼 클릭 핸들러
  const handleReject = (recordId) => {
    setCurrentRecordId(recordId);
    setEditCategory('내 논문이 아닙니다.');
    setEditContent('');
    setEditModalOpen(true);
  };

  // 수정 요청 팝업 저장 핸들러
  const handleEditSubmit = async () => {
    if (!currentRecordId) return;

    // 표시 라벨 → API 카테고리 값 (저자/학과 정보는 이전과 동일)
    const categoryMap = {
      '내 논문이 아닙니다.': '내 논문이 아닙니다.',
      '내 논문은 맞지만, 나의 정보에 오류가 있습니다.': '저자 정보가 틀렸습니다.',
      '내 논문은 맞지만, 공동저자의 정보에 오류가 있습니다.': '학과 정보가 틀렸습니다.',
      '기타': '기타',
    };
    const category = categoryMap[editCategory] ?? editCategory;
    const payload = {
      category: category,
      content: editContent.trim(),
      title: category,
      kboard_option_resource_id: String(currentRecordId),
      record_id: String(currentRecordId),
    };
    if (editCategory !== '기타') {
      payload.self_confirm = editCategory === '내 논문이 아닙니다.' ? 'N' : 'Y';
    }

    if (!editContent.trim()) {
      alert('문의 내용을 작성해주세요.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await postData('/rm/v1/my_library/resource', payload);

      if (res?.success === true || res?.status === true) {
        setEditModalOpen(false);
        setSuccessMessage('수정 요청되었습니다');
        setSuccessModalOpen(true);
        await fetchList(page);
      }
    } catch (e) {
      alert('처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={tabRef} className="active pt-[50px] lg:pt-[60px]">
      {err && <div className="mb-4 text-red-600 text-sm">에러: {String(err)}</div>}
      <h3 className="mb-4 lg:mb-6 text-[18px] font-bold leading-[100%]">논문 확인 현황</h3>

      <div className="flex justify-between mb-6 lg:mb-8 py-4 lg:py-[30px] text-center bg-[#F3F5F9] rounded-[12px]">
        <div className="flex flex-col lg:flex-col-reverse gap-[10px] lg:gap-2 w-[32%]">
          <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">전체 <br className="lg:hidden" />논문수</p>
          <p className="text-[20px] font-black">{totalPapers ?? '--'}</p>
        </div>
        <div className="w-[1px] bg-[#E4E8F1]"></div>
        <div className="flex flex-col lg:flex-col-reverse gap-[10px] lg:gap-2 w-[32%]">
          <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">저자가 확인한 <br className="lg:hidden" />논문수</p>
          <p className="text-[20px] text-[#4B4DFF] font-black">{confirmedPapers}</p>
        </div>
        <div className="w-[1px] bg-[#E4E8F1]"></div>
        <div className="flex flex-col lg:flex-col-reverse gap-[10px] lg:gap-2 w-[32%]">
          <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">미확인 <br className="lg:hidden" />논문수</p>
          <p className="text-[20px] text-[#F3652D] font-black">{unconfirmedPapers}</p>
        </div>
      </div>

      <div className="flex gap-[22px] mb-6 lg:mb-8">
        <label className="form-wrap group inline-flex cursor-pointer select-none">
          <input type="radio" name="publica-Filter" className="peer hidden" checked={filter === 'all'} onChange={() => setFilter('all')} />
          <span className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-full group-hover:border-[#4B4DFF] peer-checked:border-[4px] peer-checked:border-[#4B4DFF] peer-checked:bg-[#fff] transition-colors duration-200 translate-y-[3px]">
            <span className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"></span>
          </span>
          <span className="ml-[10px] text-[14px] text-[#6C7687] translate-y-[1px]">전체 논문</span>
        </label>
        <label className="form-wrap group inline-flex cursor-pointer select-none">
          <input type="radio" name="publica-Filter" className="peer hidden" checked={filter === 'confirmed'} onChange={() => setFilter('confirmed')} />
          <span className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-full group-hover:border-[#4B4DFF] peer-checked:border-[4px] peer-checked:border-[#4B4DFF] peer-checked:bg-[#fff] transition-colors duration-200 translate-y-[3px]">
            <span className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"></span>
          </span>
          <span className="ml-[10px] text-[14px] text-[#6C7687] translate-y-[1px]">확인된 논문</span>
        </label>
        <label className="form-wrap group inline-flex cursor-pointer select-none">
          <input type="radio" name="publica-Filter" className="peer hidden" checked={filter === 'unconfirmed'} onChange={() => setFilter('unconfirmed')} />
          <span className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-full group-hover:border-[#4B4DFF] peer-checked:border-[4px] peer-checked:border-[#4B4DFF] peer-checked:bg-[#fff] transition-colors duration-200 translate-y-[3px]">
            <span className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"></span>
          </span>
          <span className="ml-[10px] text-[14px] text-[#6C7687] translate-y-[1px]">미확인 논문</span>
        </label>
      </div>

      <div className="relative">
        {/* 첫 로드 또는 데이터 없을 때만 스켈레톤, 탭 전환 시에는 기존 데이터 유지 */}
        {loading && rows.length === 0 && <ChartSkeletonLoader show={loading} />}

        {displayRows.length > 0 ? (
          <div className="flex flex-col gap-4">
            {displayRows.map((item) => {
              const recordId = item.record_id || item.resource_id || item.id;
              const sc = getSelfConfirm(item, userId);
              const isConfirmed = sc === 'Y' || sc === 'y' || sc === true;
              const isRejected = sc === 'N' || sc === 'n';
              const hasScValue = sc != null && sc !== '';
              const showQuestionButtons = !hasScValue;
              const showYesBtn = sc == null || sc === '' || isRejected;
              const showNoBtn = sc == null || sc === '' || isConfirmed;
              const qnaStatus = Number(item?.qna_status) || 0;
              const qnaActiveUid = Number(item?.qna_active_content_id) || 0;
              const qnaLabel = qnaStatus === 1 ? '문의중' : qnaStatus === 2 ? '처리 완료' : '';
              const qnaBaseUrl = String(rmVars?.homeUrl || '').replace(/\/$/, '');
              const qnaHref = qnaActiveUid ? `${qnaBaseUrl}/qna/?list_mod=member_list&pageid=1&mod=document&uid=${qnaActiveUid}` : '';
              const qnaNewInquiryHref = `${qnaBaseUrl}/qna/?list_mod=member_list&pageid=1&mod=editor`;
              const showQnaSection = (qnaStatus === 1 || qnaStatus === 2) && (qnaActiveUid > 0 || qnaStatus === 2);
              const showInquiryLink = hasScValue && !showQnaSection;

              return (
                <div key={recordId} className="bg-white border border-[#E4E8F1] rounded-xl overflow-hidden hover:shadow-[0px_4px_20px_rgba(0,0,0,0.1)] transition duration-300">
                  <PublicationCard item={item} showAltmetric={false} />
                  {(showQuestionButtons || showQnaSection || showInquiryLink) && (
                    <div className="flex flex-col lg:flex-row justify-center items-center gap-x-6 gap-y-[14px] px-8 py-5 bg-[#F3F5F9] border-t border-[#E4E8F1]">
                      {showQuestionButtons && (
                        <>
                          <p className="text-[14px] lg:text-[15px] font-bold text-[#333] text-center lg:text-left">
                            내 논문이며, 공동연구자의 정보가 정확한가요?
                          </p>
                          <div className="flex justify-center items-center gap-[10px]">
                            {showYesBtn && (
                              <button
                                onClick={() => handleConfirm(recordId)}
                                disabled={submitting}
                                className="w-[65px] lg:w-[68px] h-[32px] bg-[#4B4DFF] text-white text-[14px] font-bold cursor-pointer rounded-full hover:bg-[#3335FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                예
                              </button>
                            )}
                            {showNoBtn && (
                              <button
                                onClick={() => handleReject(recordId)}
                                disabled={submitting}
                                className="w-[65px] lg:w-[68px] h-[32px] bg-[#4B4DFF] text-white text-[14px] font-bold cursor-pointer rounded-full hover:bg-[#3335FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                아니오
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      {showQnaSection && (
                        <div className="flex flex-wrap items-center gap-3">
                          <span className={`inline-flex items-center h-[24px] px-2 rounded-full text-[12px] font-semibold ${qnaStatus === 2 ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#DBEAFE] text-[#1D4ED8]'}`}>
                            {qnaLabel}
                          </span>
                          {qnaActiveUid > 0 && (
                            <a href={qnaHref} target="_blank" rel="noreferrer" className="text-[13px] font-bold text-[#215BD8] underline underline-offset-2 hover:text-[#1747A8]">
                              문의 바로가기
                            </a>
                          )}
                          {qnaStatus === 2 && (
                            <a href={qnaNewInquiryHref} target="_blank" rel="noreferrer" className="text-[13px] font-bold text-[#215BD8] underline underline-offset-2 hover:text-[#1747A8]">
                              새롭게 문의하기
                            </a>
                          )}
                        </div>
                      )}
                      {showInquiryLink && (
                        <a href={qnaNewInquiryHref} target="_blank" rel="noreferrer" className="text-[13px] font-bold text-[#215BD8] underline underline-offset-2 hover:text-[#1747A8]">
                          문의하기
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="text-center py-8 text-[#8D9AAF]">데이터가 없습니다.</div>
          )
        )}
      </div>

      <div className="mt-8">
        <Paginator pageInfo={pageInfo} onPage={onPage} />
      </div>

      {/* 수정 요청 팝업 */}
      {editModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[100]" onClick={() => !submitting && setEditModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[600px] w-[calc(100%-80px)] z-[101] bg-white rounded-xl p-6 shadow-[0px_4px_20px_0px_rgba(0,_0,_0,_0.1)]">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-[18px] font-bold">논문정보 수정 요청</h4>
              <button onClick={() => !submitting && setEditModalOpen(false)} className="cursor-pointer" disabled={submitting}>
                <img src={`${rmVars.themeUrl}images/clear-icon-333.png`} className="w-3" alt="close" />
              </button>
            </div>
            <div className="flex flex-col gap-4 mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editCategory" checked={editCategory === '내 논문이 아닙니다.'} onChange={() => setEditCategory('내 논문이 아닙니다.')} className="w-4 h-4" />
                <span className="text-[14px]">내 논문이 아닙니다.</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editCategory" checked={editCategory === '내 논문은 맞지만, 나의 정보에 오류가 있습니다.'} onChange={() => setEditCategory('내 논문은 맞지만, 나의 정보에 오류가 있습니다.')} className="w-4 h-4" />
                <span className="text-[14px]">내 논문은 맞지만, 나의 정보에 오류가 있습니다.</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editCategory" checked={editCategory === '내 논문은 맞지만, 공동저자의 정보에 오류가 있습니다.'} onChange={() => setEditCategory('내 논문은 맞지만, 공동저자의 정보에 오류가 있습니다.')} className="w-4 h-4" />
                <span className="text-[14px]">내 논문은 맞지만, 공동저자의 정보에 오류가 있습니다.</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editCategory" checked={editCategory === '기타'} onChange={() => setEditCategory('기타')} className="w-4 h-4" />
                <span className="text-[14px]">기타</span>
              </label>
            </div>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="문의 내용을 작성해주세요."
              className="w-full h-[120px] px-4 py-3 border border-[#E4E8F1] rounded-lg text-[14px] outline-none resize-none mb-6"
              disabled={submitting}
            />
            <div className="flex justify-center gap-3">
              <button onClick={() => !submitting && setEditModalOpen(false)} disabled={submitting} className="px-6 py-2 border border-[#E4E8F1] rounded-lg text-[14px] font-medium text-[#6C7687] bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                취소
              </button>
              <button onClick={handleEditSubmit} disabled={submitting} className="px-6 py-2 bg-[#4B4DFF] rounded-lg text-[14px] font-medium text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                저장
              </button>
            </div>
          </div>
        </>
      )}

      {/* 성공 팝업 */}
      {successModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[100]" onClick={() => setSuccessModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[400px] w-[calc(100%-80px)] z-[101] bg-white rounded-xl p-6 shadow-[0px_4px_20px_0px_rgba(0,_0,_0,_0.1)]">
            <div className="text-center">
              <p className="text-[16px] font-semibold mb-4">{successMessage}</p>
              <button onClick={() => setSuccessModalOpen(false)} className="px-6 py-2 bg-[#4B4DFF] rounded-lg text-[14px] font-medium text-white cursor-pointer">
                확인
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
